import React, { useState } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import { SCENARIO_FIELDS, specialistScenario } from '../lib/specialistScenario.js'
import { emptyPlanPayload, planFingerprint } from '../lib/planDrafts.js'
import PlanDraftControls, { usePlanDraft } from './PlanDraftControls.jsx'
import PlateSpecialistDraftControls, { usePlateSpecialistDraft } from './PlateSpecialistDraft.jsx'
import './SpecialistMarketReview.css'

const LABELS = {
  exitPrice: 'Assumed exit price (€)', importCosts: 'Import and registration costs (€)',
  repairs: 'Repair and preparation allowance (€)', sellingCosts: 'Selling fees and costs (€)',
  monthlyHolding: 'Holding and finance costs per month (€)', months: 'Holding period (months)',
  requiredProfit: 'Required profit (€)',
}
const euro = value => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value)

export default function SpecialistMarketReview(props) {
  // Plate budgets have their own input-only binding, never a foreign listing ID.
  return props.draftCar
    ? <SavedSpecialistReview key={planFingerprint(props.draftCar, 'specialist', props.draftContext)} {...props} />
    : props.carInfo?.plate ? <PlateSpecialistReview key={`${props.carInfo.plate}:${props.mileageKm}`} {...props} />
      : <SessionSpecialistReview {...props} />
}

function PlateSpecialistReview(props) {
  const draft = usePlateSpecialistDraft(props.carInfo.plate, props.mileageKm)
  return <SpecialistReviewForm {...props} draft={draft} plateDraft />
}

function SavedSpecialistReview(props) {
  const draft = usePlanDraft(props.draftCar, 'specialist', props.draftContext)
  return <SpecialistReviewForm {...props} draft={draft} />
}

function SessionSpecialistReview(props) {
  const [payload, setPayload] = useState(() => emptyPlanPayload(null, 'specialist'))
  return <SpecialistReviewForm {...props} draft={{ payload, setPayload }} />
}

function SpecialistReviewForm({ carInfo, row, mileageKm, askingEvidence, askingPrice, draft, draftCar, plateDraft, evidenceCurrent = true }) {
  const { t } = useTranslation()
  const { inputs: values, choices } = draft.payload
  const setValues = update => draft.setPayload(previous => ({ ...previous, inputs: update(previous.inputs) }))
  const choose = key => event => draft.setPayload(previous => ({ ...previous, choices: { ...previous.choices, [key]: event.target.value } }))
  const scenario = evidenceCurrent && !draft.needsReview ? specialistScenario(values) : null
  const low = scenario ? Math.min(0, ...scenario.points.map(p => p.maximumBid)) : 0
  const high = scenario ? Math.max(1, ...scenario.points.map(p => p.maximumBid)) : 1
  const maxMonths = scenario ? Math.max(1, ...scenario.points.map(p => p.months)) : 12
  const x = months => 65 + months / maxMonths * 390
  const y = bid => 160 - (bid - low) / (high - low) * 130

  return <section className="specialist-review" aria-labelledby="specialist-title">
    <h3 id="specialist-title">{t('Specialist market review')}</h3>
    <p>{t('Age and rarity do not establish demand. Condition, exact specification, provenance and selling time can dominate mileage for an enthusiast or classic car.')}</p>
    <div className="kenteken-caveat" role="status">
      <strong>{t('No automated specialist valuation')}</strong><br />
      {t('Verified Dutch sale prices, inspected condition and calibrated sale probabilities are not available. This panel provides asking-price context and your own scenarios, not a buy recommendation.')}
    </div>
    <div className="specialist-evidence">
      <div><strong>{t('Identity and odometer')}</strong>
        <p>{carInfo?.merk} {carInfo?.handelsbenaming} · {carInfo?.year} · {mileageKm > 0 ? Math.round(mileageKm).toLocaleString('nl-NL') : t('Unknown')} km</p>
        <label htmlFor="specialist-odometer">{t('Odometer evidence')}</label>
        <select id="specialist-odometer" className="kenteken-field" value={choices.odometer} onChange={choose('odometer')}>
          <option value="unknown">{t('Unknown or unverified')}</option>
          <option value="documented">{t('Documented reading, not verified by AutoMargin')}</option>
          <option value="replaced">{t('Replaced, rolled over or uncertain lifetime mileage')}</option>
        </select>
      </div>
      <div><strong>{t('Condition and provenance')}</strong>
        <label htmlFor="specialist-condition">{t('Condition assumption, not an inspection')}</label>
        <select id="specialist-condition" className="kenteken-field" value={choices.condition} onChange={choose('condition')}>
          <option value="unknown">{t('Unknown or unverified')}</option>
          <option value="project">{t('Project or restoration required')}</option>
          <option value="driver">{t('Usable driver')}</option>
          <option value="documented">{t('Documented high-quality example')}</option>
        </select>
        <p>{t('Verify corrosion, mechanical condition, restoration quality, originality, service records and the exact generation. A seller claim is not an inspection.')}</p>
      </div>
    </div>
    <div className="specialist-context">
      <h4>{t('Dutch asking-price context')}</h4>
      {askingEvidence || (row ? <>
        <p>{row.make} {row.model} · {row.fuel} · {row.hp_band || t('all engine powers')} · {row.n_listings} {t('listings')}</p>
        <p>{t('middle half')}: {euro(row.p25)} – {euro(row.p75)} · {t('90% range')}: {euro(row.p5)} – {euro(row.p95)}</p>
        <p>{t('Whole published group across its model years and mileages, not condition-matched or a value for this car. Exact generation, duplicates and source freshness still require review.')}</p>
        <small>{t('Group built at')}: {row.computed_at || t('Unknown')} · {t('Build time is not the listing observation date.')}</small>
      </> : <p>{t('No published Dutch asking-price group matched. Missing evidence is not zero value.')}</p>)}
    </div>
    <h4>{t('Conditional purchase budget')}</h4>
    <p>{t('Enter every assumption, including an explicit zero where appropriate. These inputs are not learned or verified by AutoMargin and do not affect the published model.')}</p>
    <label htmlFor="specialist-channel">{t('Assumed exit channel')}</label>
    <select id="specialist-channel" className="kenteken-field" value={choices.exitChannel} onChange={choose('exitChannel')}>
      <option value="nl">{t('Dutch retail')}</option>
      <option value="trade">{t('Trade or consignment')}</option>
      <option value="international">{t('International enthusiast buyer, not an NL resale estimate')}</option>
    </select>
    <div className="specialist-inputs">
      {SCENARIO_FIELDS.map(field => <div className="kenteken-input-group" key={field}>
        <label htmlFor={`specialist-${field}`}>{t(LABELS[field])}</label>
        <input id={`specialist-${field}`} type="number" inputMode="decimal" min="0"
          max={field === 'months' ? 120 : 1e9} step={field === 'months' ? '0.1' : '0.01'}
          className="kenteken-field" value={values[field]}
          onChange={event => setValues(previous => ({ ...previous, [field]: event.target.value }))} />
        {field === 'importCosts' && <small>{t('Include transport, BPM, applicable VAT and customs, registration and insurance. This calculator does not determine tax treatment.')}</small>}
      </div>)}
    </div>
    {scenario ? <div className="specialist-result" aria-live="polite">
      <strong>{scenario.maximumBid > 0 ? `${t('Conditional maximum purchase price')}: ${euro(scenario.maximumBid)}`
        : `${t('No positive purchase budget')}: ${t('shortfall')} ${euro(-scenario.maximumBid)}`}</strong>
      {askingPrice > 0 && <p>{t('Foreign asking price')}: {euro(askingPrice)} · {t('Difference from your maximum bid')}: {euro(scenario.maximumBid - askingPrice)}</p>}
      <p>{t('Exit price minus import, repairs, selling costs, holding costs and required profit. Do not include the purchase price in the cost inputs or count a cost twice.')}</p>
      <h4>{t('Purchase budget versus holding time')}</h4>
      <svg viewBox="0 0 490 200" role="img" aria-label={t('User-assumption scenario, not a forecast of demand or depreciation')}>
        <line x1="65" y1={y(0)} x2="455" y2={y(0)} stroke="#94a3b8" />
        <polyline points={scenario.points.map(p => `${x(p.months)},${y(p.maximumBid)}`).join(' ')} fill="none" stroke="#2563eb" strokeWidth="2" />
        <text x="60" y={y(high) + 4} textAnchor="end">{euro(high)}</text>
        <text x="60" y={y(low) + 4} textAnchor="end">{euro(low)}</text>
        {scenario.points.map(p => <g key={p.months}>
          <circle cx={x(p.months)} cy={y(p.maximumBid)} r={p.months === scenario.assumptions.months ? 5 : 3} fill="#2563eb"><title>{p.months} {t('months')}: {euro(p.maximumBid)}</title></circle>
          <text x={x(p.months)} y="184" textAnchor="middle">{p.months}</text>
        </g>)}
        <text x="270" y="199" textAnchor="middle">{t('Holding period (months)')}</text>
      </svg>
      <table><caption>{t('Same exit price and repairs, different holding periods')}</caption>
        <thead><tr><th>{t('Holding period (months)')}</th><th>{t('Conditional maximum purchase price')}</th></tr></thead>
        <tbody>{scenario.points.map(p => <tr key={p.months}><td>{p.months}</td><td>{euro(p.maximumBid)}</td></tr>)}</tbody>
      </table>
    </div> : <p className="kenteken-caveat">{t('Complete all non-negative assumptions to calculate. No price or cost is filled in for you.')}</p>}
    {draftCar && <div className="specialist-draft-controls"><PlanDraftControls draft={draft}
      saveLabel="Save specialist scenario on this device" updateLabel="Update saved specialist scenario" />
      <p>{t('Saved condition, odometer and exit-channel choices are your assumptions, not verified evidence. Changed published data or a saved review over a day old clears the exit price, import quote and condition/odometer choices before reuse.')}</p>
    </div>}
    {plateDraft && <PlateSpecialistDraftControls draft={draft} />}
    <p className="specialist-limit">{t('Liquidity, condition premiums and valuation confidence remain unmeasured. A niche market needs verified outcomes over 30, 90 and 180 days; no weekly exits does not mean no demand.')}
      {' '}{t(draftCar || plateDraft ? 'Save or download your scenario before leaving. Unsaved edits are not kept.' : 'This plate-only specialist scenario stays in this session only.')}</p>
  </section>
}
